import {PhoneHQGlyph} from 'ch3-ui-lib';
import React, { useState, useRef } from 'react';

interface MediaUploadProps {
  onUploadComplete?: () => void;
  onClose?: () => void;
  onFileSelect?: (file: File) => void;
}

const MediaUpload: React.FC<MediaUploadProps> = ({
                                                   onUploadComplete,
                                                   onClose,
                                                   onFileSelect
                                                 }) => {
  const [dragActive, setDragActive] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleFileSelect = (file: File) => {
    // Validate file size (50MB limit)
    if (file.size > 50 * 1024 * 1024) {
      setError('File size must be less than 50MB');
      return;
    }

    // Validate file type
    const allowedTypes = [
      'image/jpeg', 'image/png', 'image/gif', 'image/webp',
      'video/mp4', 'video/webm', 'video/mov',
      'audio/mp3', 'audio/wav', 'audio/ogg', 'audio/webm',
      'application/pdf', 'text/plain', 'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ];

    if (!allowedTypes.includes(file.type)) {
      setError('File type not supported');
      return;
    }

    setSelectedFile(file);
    setError(null);
  };

  const handleUpload = async () => {
    if (!selectedFile || !onFileSelect) return;

    setIsUploading(true);
    setError(null);

    try {
      await onFileSelect(selectedFile);
      if (onUploadComplete) {
        onUploadComplete();
      }
    } catch (error) {
      console.error('Upload failed:', error);
      setError('Failed to upload file. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (type: string): string => {
    if (type.startsWith('image/')) return 'image';
    if (type.startsWith('video/')) return 'movie';
    if (type.startsWith('audio/')) return 'audiotrack';
    if (type.includes('pdf')) return 'picture_as_pdf';
    if (type.includes('document') || type.includes('word')) return 'description';
    return 'attachment';
  };

  return (
      <div className="bg-surface border border-line rounded-lg shadow-lg p-6 max-w-md w-full">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-ink">Upload Media</h3>
          <button
              onClick={onClose}
              disabled={isUploading}
              className="p-1 text-ink-muted hover:text-ink-secondary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              data-tooltip="Close"
          >
            <PhoneHQGlyph name="close" size={24}/>
          </button>
        </div>

        {/* Error Display */}
        {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center space-x-2">
                <PhoneHQGlyph name="error" size={14} className="text-red-600"/>
                <span className="text-red-800 text-sm">{error}</span>
              </div>
            </div>
        )}

        {/* File Upload Area */}
        <div
            className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer ${
                dragActive
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-fill-subtle hover:border-fill-subtle'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
        >
          <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.txt"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  handleFileSelect(file);
                }
              }}
              disabled={isUploading}
          />

          {selectedFile ? (
              <div className="space-y-3">
                <div className="flex items-center justify-center space-x-3">
                  <div className="flex-shrink-0">
                    <PhoneHQGlyph name={getFileIcon(selectedFile.type)} size={30} className="text-ink-muted"/>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-ink truncate">
                      {selectedFile.name}
                    </p>
                    <p className="text-sm text-ink-muted">
                      {formatFileSize(selectedFile.size)}
                    </p>
                    <p className="text-xs text-ink-muted uppercase">
                      {selectedFile.type.split('/')[1] || selectedFile.type}
                    </p>
                  </div>
                </div>
                <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedFile(null);
                      setError(null);
                    }}
                    className="text-sm text-ink-muted hover:text-ink-secondary"
                    disabled={isUploading}
                >
                  Choose different file
                </button>
              </div>
          ) : (
              <div className="space-y-3">
                <div className="flex justify-center">
                  <PhoneHQGlyph name="cloud_upload" size={36} className="text-ink-muted"/>
                </div>
                <div>
                  <p className="text-ink-muted-strong">
                    <span className="font-medium">Click to upload</span> or drag and drop
                  </p>
                  <p className="text-sm text-ink-muted mt-1">
                    Images, videos, audio, or documents (max 50MB)
                  </p>
                </div>
              </div>
          )}
        </div>

        {/* Upload Button */}
        {selectedFile && (
            <div className="mt-6 flex items-center justify-end space-x-3">
              <button
                  onClick={onClose}
                  disabled={isUploading}
                  className="px-4 py-2 text-ink-muted-strong hover:text-ink disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Cancel
              </button>
              <button
                  onClick={handleUpload}
                  disabled={isUploading}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
              >
                {isUploading ? (
                    <>
                      <PhoneHQGlyph name="refresh" size={14} className="animate-spin"/>
                      <span>Uploading...</span>
                    </>
                ) : (
                    <>
                      <PhoneHQGlyph name="upload" size={14}/>
                      <span>Upload</span>
                    </>
                )}
              </button>
            </div>
        )}

        {/* Upload Info */}
        <div className="mt-4 p-3 bg-canvas rounded-lg">
          <div className="flex items-center space-x-2">
            <PhoneHQGlyph name="cloud_upload" size={14} className="text-blue-600"/>
            <span className="text-sm text-ink-secondary">
            Files are uploaded securely to the chat server
          </span>
          </div>
        </div>
      </div>
  );
};

export default MediaUpload;